import { useState, useEffect } from 'react';
import axios from 'axios';
import { applyThemePreset, applyNavStyle, DEFAULT_THEME_PRESET, DEFAULT_NAV_STYLE } from '../theme/presets';

const SETTINGS_CACHE_KEY = 'app_settings_cache';

export const useSettings = () => {
  // Build-time branding injection - no API calls needed
  const staticBranding = {
    product_name: process.env.REACT_APP_PRODUCT_NAME || 'Apollo Exporter',
    product_logo: process.env.REACT_APP_PRODUCT_LOGO_URL || ''
  };

  // Initialize with cached settings from localStorage or empty object
  const [settings, setSettings] = useState(() => {
    try {
      const cached = localStorage.getItem(SETTINGS_CACHE_KEY);
      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      console.error('Error parsing cached settings:', error);
      return {};
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(() => {
    // Check if we have cached settings (only need non-branding settings now)
    const cached = localStorage.getItem(SETTINGS_CACHE_KEY);
    return !!cached;
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
        
        // Fetch only non-branding settings (timezone, email_enabled, billing_mode, favicon)
        const settingsResponse = await axios.get(`${API_BASE_URL}/api/settings/`);
        const newSettings = settingsResponse.data;
        
        // Update settings
        setSettings(newSettings);
        
        // Cache in localStorage
        localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(newSettings));
        
        setSettingsLoaded(true);
      } catch (error) {
        console.error('Error fetching settings:', error);
        // If we have cached settings, use them; otherwise use defaults
        if (!settingsLoaded) {
          const defaultSettings = {
            timezone: 'UTC',
            email_enabled: false,
            billing_mode: 'user_subscription',
            favicon_data_url: null
          };
          setSettings(defaultSettings);
        }
        setSettingsLoaded(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [settingsLoaded]);

  // Combine settings with static branding for backward compatibility
  const combinedSettings = {
    ...settings,
    ...staticBranding  // Static branding always takes precedence
  };

  // Apply favicon at runtime if provided
  useEffect(() => {
    try {
      const href = combinedSettings.favicon_data_url;
      if (!href) return;
      const linkId = 'dynamic-favicon-link';
      let link = document.getElementById(linkId);
      if (!link) {
        link = document.createElement('link');
        link.id = linkId;
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = href;
    } catch (e) {
      console.error('Failed to apply favicon:', e);
    }
  }, [combinedSettings.favicon_data_url]);

  // Apply the theme preset at runtime.
  //
  // The boot script in public/index.html has already applied the previously
  // stored preset before first paint; this reconciles it with what the server
  // now says and persists the new value for the next cold start.
  //
  // This hook is called independently by ~12 components, so this effect runs
  // several times per page. That is harmless: setting an attribute to the value
  // it already has is idempotent and triggers no restyle. Hoisting the fetch
  // into a context would be a much wider change across out-of-scope files.
  useEffect(() => {
    if (!settingsLoaded) return;
    applyThemePreset(combinedSettings.theme_preset || DEFAULT_THEME_PRESET);
    applyNavStyle(combinedSettings.nav_style || DEFAULT_NAV_STYLE);
  }, [combinedSettings.theme_preset, combinedSettings.nav_style, settingsLoaded]);

  return {
    settings: combinedSettings, // For backward compatibility
    branding: staticBranding, // New separate static branding object
    loading, 
    settingsLoaded,
    // Provide safe fallback for product_name to prevent flash
    safeSettings: {
      ...combinedSettings,
      product_name: staticBranding.product_name, // Always available at build time
      product_logo: staticBranding.product_logo, // Always available at build time
      timezone: settingsLoaded ? settings.timezone : (settings.timezone || 'UTC')
    }
  };
};